import { inject, Injectable } from '@angular/core';
import {
  FilterValues,
  FinancialAccountActivities,
  FinancialAccountActivitiesRaw,
  FinancialAccountCategory,
  FinancialAccountDetails,
  FinancialAccountJournalActivities,
  FinancialAccountJournalActivitiesRaw,
  FinancialAccountLimitsRaw,
  FinancialAccountLimitsUpdateResponse,
  FinancialAccountListRequest,
  FinancialAccountRestrictionUpdateParams,
  FinancialAccountUpdateData,
  LimitItem,
  LimitType,
  LimitUpdateRequest,
  RequestPageParams,
} from '@shared/models';
import { BehaviorSubject, catchError, exhaustMap, map, Observable, of, throwError } from 'rxjs';
import { StorageService } from '../storage/storage.service';
import { constants } from '@shared/constants';
import { HttpContext, HttpHeaders, HttpParams } from '@angular/common/http';
import { BackendService } from '../backend/backend.service';
import { environment } from '@env';
import { FinancialAccountDetailsRaw, FinancialAccountList, FinancialAccountListRaw } from '@shared/models';
import { limitDisplayNames, limitTypes } from '@shared/utils';
import {
  financialAccountActivitiesRequestParams,
  financialAccountsRequestParams,
  mapFinancialAccountActivities,
  mapFinancialAccountDetails,
  mapFinancialAccountList,
} from './financial-account-mapping-utils';
import { IS_EXTENDED_ERRORS } from '@shared/interceptors';
import { sortBy } from 'lodash-es';

@Injectable({
  providedIn: 'root',
})
export class FinancialAccountService {
  backendService = inject(BackendService);

  storageService = inject(StorageService);

  financialAccountListFilter = new BehaviorSubject<FilterValues>({});

  setFinancialAccountFilterParams(filters: FilterValues): void {
    this.storageService.setItem(constants.KOR_FINANCIAL_ACCOUNT_FILTER, filters);
    this.updateFinancialAccountFilterParams(filters);
  }

  getFinancialAccountFilterParams(): void {
    const filters = this.storageService.getItem<FilterValues>(constants.KOR_FINANCIAL_ACCOUNT_FILTER);

    if (filters && Object.keys(filters).length) {
      this.updateFinancialAccountFilterParams(filters);
    }
  }

  private updateFinancialAccountFilterParams(filters: FilterValues): void {
    this.financialAccountListFilter.next(filters);
  }

  getFinancialAccounts(requestParams: FinancialAccountListRequest): Observable<FinancialAccountList> {
    const params = financialAccountsRequestParams(requestParams);

    return this.backendService.get<FinancialAccountListRaw>(environment.financialAccountService, { params }).pipe(
      map((response) => mapFinancialAccountList(response)),
      catchError((errorRes) => throwError(() => errorRes)),
    );
  }

  getFinancialAccountById(financialAccountId: string): Observable<FinancialAccountDetails> {
    return this.backendService
      .get<FinancialAccountDetailsRaw>(`${environment.financialAccountService}/${financialAccountId}?embed=balances`)
      .pipe(
        map((response) => mapFinancialAccountDetails(response)),
        catchError((errorRes) => throwError(() => errorRes)),
      );
  }

  createFinancialAccount({
    financialAccountData,
    category,
  }: {
    financialAccountData: {
      businessAccountId: string;
      name: string;
      parentId: string;
      nameOnAccount?: string;
      restrictions?: string[];
    };
    category: FinancialAccountCategory;
  }): Observable<FinancialAccountDetailsRaw> {
    const url = category === 'INTERNAL' ? 'kor-bank-accounts' : 'biz-account-escrow-financial-accounts';
    return this.backendService.post<FinancialAccountDetailsRaw>(`/v1/${url}`, financialAccountData, {
      context: new HttpContext().set(IS_EXTENDED_ERRORS, true),
    });
  }

  updateFinancialAccount({
    updateData,
    id,
  }: {
    updateData: FinancialAccountUpdateData;
    id: string;
  }): Observable<FinancialAccountDetailsRaw> {
    const headers = new HttpHeaders().set('KOR-Context', 'OPS');

    return this.backendService.update<FinancialAccountDetailsRaw>(`/v1/financial-accounts/${id}`, updateData, {
      context: new HttpContext().set(IS_EXTENDED_ERRORS, true),
      headers,
    });
  }

  updateFinancialAccountStatus(financialAccountId: string, action: string): Observable<FinancialAccountDetailsRaw> {
    return this.backendService.update<FinancialAccountDetailsRaw>(`${environment.financialAccountService}/${financialAccountId}/${action}`);
  }

  updateFinancialAccountRestrictions(updateParams: FinancialAccountRestrictionUpdateParams): Observable<FinancialAccountDetailsRaw> {
    return this.backendService.update<FinancialAccountDetailsRaw>(
      `/v1/financial-accounts/${updateParams.financialAccountId}/restrictions`,
      { restrictions: updateParams.restrictions },
      { context: new HttpContext().set(IS_EXTENDED_ERRORS, true) },
    );
  }

  getEscrowAccount(businessAccountId: string): Observable<FinancialAccountDetails> {
    const params = new HttpParams().set('page', 0).set('size', 20).set('accountRoles', 'ESCROW').set('accountHolderId', businessAccountId);

    return this.backendService.get<FinancialAccountListRaw>(environment.financialAccountService, { params }).pipe(
      exhaustMap((financialAccountsResponse: FinancialAccountListRaw) => {
        const escrowFinancialAccountId = financialAccountsResponse?.content?.[0]?.id;
        if (!escrowFinancialAccountId) {
          return of();
        }

        return this.getFinancialAccountById(escrowFinancialAccountId);
      }),
      catchError((errorRes) => throwError(() => errorRes)),
    );
  }

  getFinancialAccountActivities(requestParams: RequestPageParams, financialAccountId: string): Observable<FinancialAccountActivities> {
    const params = financialAccountActivitiesRequestParams(requestParams);

    return this.backendService
      .get<FinancialAccountActivitiesRaw>(`${environment.financialAccountService}/${financialAccountId}/activities`, { params })
      .pipe(
        map((response) => mapFinancialAccountActivities(response)),
        catchError((errorRes) => throwError(() => errorRes)),
      );
  }

  getJournalActivity(requestParams: RequestPageParams, financialAccountId: string): Observable<FinancialAccountJournalActivities> {
    const params = financialAccountActivitiesRequestParams(requestParams);

    return this.backendService
      .get<FinancialAccountJournalActivitiesRaw>(`${environment.financialAccountService}/${financialAccountId}/journals?currency=USD`, {
        params,
      })
      .pipe(
        map((response) => {
          const { content = [], totalElements = 0 } = response ?? {};
          return { items: content, totalElements };
        }),
        catchError((errorRes) => throwError(() => errorRes)),
      );
  }

  getFinancialAccountLimits({ financialAccountId }: { financialAccountId: string }): Observable<LimitItem[]> {
    return this.backendService.get<FinancialAccountLimitsRaw>(`${environment.financialAccountService}/${financialAccountId}/limits`).pipe(
      map((limits) => {
        const newLimits = limits.periods.map((item) => {
          const currency = Object.keys(item.amount)[0] ?? 'USD';
          const { limit, used } = item.amount[currency];
          const limitType = item.type.toUpperCase() as LimitType;
          return {
            id: item.type,
            limitType,
            label: limitDisplayNames[limitType],
            amount: limit,
            used,
            progress: Number(used) ? (Number(used) / Number(limit)) * 100 : 0,
            currency,
          } as unknown as LimitItem;
        });
        return sortBy(newLimits, (item) => limitTypes.indexOf(item.limitType));
      }),
    );
  }

  updateFinancialAccountLimits(financialAccountId: string, limits: LimitUpdateRequest): Observable<FinancialAccountLimitsUpdateResponse> {
    return this.backendService.update<FinancialAccountLimitsUpdateResponse>(
      `${environment.financialAccountService}/${financialAccountId}/limits`,
      limits,
      { context: new HttpContext().set(IS_EXTENDED_ERRORS, true) },
    );
  }
}
